import { useState, useEffect } from 'react';
import { useTranslation, Trans } from 'next-i18next';
import { useForm } from 'react-hook-form';
import classNames from 'classnames';
import { Button, Input, PhoneInput, Textarea, phonemasks } from '@forma/forma-ui-kit';
import Link from '@/components/Link';
import ControlField from '@/components/fields/ControlField';

import styles from './team-request-form.module.css';

const TeamRequestForm = ({ onSubmit, isSendSuccess, isSendError }) => {
  const { t, i18n } = useTranslation();
  const { register, handleSubmit, control, formState: { isValid, errors } } = useForm({
    mode: 'onChange',
  });
  const [phoneValidation, setPhoneValidation] = useState('');

  useEffect(() => {
    if (i18n && phonemasks) {
      const code = i18n.language === 'en' ? 'US' : i18n.language.toUpperCase();
      const mask = phonemasks.find(({ iso }) => iso === code);
      if (mask) setPhoneValidation(mask.code + mask.mask);
    }
  }, [i18n]);

  const handleChangeMask = ({ mask, code }) => {
    setPhoneValidation(code + mask);
  };

  return (
    <form className={styles.root} onSubmit={handleSubmit(onSubmit)}>
      {(isSendSuccess || isSendError) ? (
        <div className={styles.result}>
          <div className={styles.resultTitle}>{isSendSuccess ? t('thank_you') : t('error')}</div>
          <div className={styles.resultSubtitle}>{isSendSuccess ? t('do_you_want_team_success') : t('do_you_want_team_error')}</div>
        </div>
      ) : (
        <>
          <div className={styles.title}>
            {t('do_you_want_team_title')}
          </div>
          <div className={styles.subtitle}>{t('do_you_want_team_description')}</div>
          <div className={styles.fields}>
            <ControlField
              id="name"
              component={Input}
              placeholder={t('your_name')}
              control={control}
              {...register('name', {
                required: t('please_fill_the_field'),
                minLength: { value: 3, message: t('errors.min_lenght_3') },
                pattern: { value: /^[^0-9]+$/, message: t('errors.name_has_numbers') }
              })}
              meta={{
                error: errors.name,
                containerClass: classNames(styles.input, styles.half)
              }}
            />
            <ControlField
              id="phone"
              component={PhoneInput}
              masks={phonemasks}
              defaultMaskCode={i18n.language === 'en' ? 'US' : i18n.language.toUpperCase()}
              onChangeMask={handleChangeMask}
              placeholder={t('phone_number')}
              control={control}
              error={errors.phone}
              {...register('phone', {
                required: t('please_fill_the_field'),
                minLength: { value: phoneValidation.length, message: t('errors.min_lenght_phone') }
              })}
              containerClass={classNames(styles.input, styles.half)}
              meta={{
                containerClass: classNames(styles.input, styles.half)
              }}
            />
            <ControlField
              id="comment"
              component={Textarea}
              placeholder={t('tell_about_yourself')}
              control={control}
              autoComplete="off"
              {...register('comment', {
                required: t('please_fill_the_field'),
                minLength: { value: 3, message: t('errors.min_lenght_3') }
              })}
              meta={{
                error: errors.comment,
                containerClass: classNames(styles.input)
              }}
            />
            <ControlField
              id="resume"
              component={Input}
              placeholder={t('link_to_resume')}
              control={control}
              autoComplete="off"
              {...register('resume', { required: t('please_fill_the_field') })}
              meta={{
                error: errors.resume,
                containerClass: classNames(styles.input)
              }}
            />
          </div>
          <div className={styles.buttons}>
            <div className={styles.notice}>
              <Trans
                i18nKey="user_agreement_form_notice"
                components={{ privacy: <Link href="/privacy" target="_blank" />, license: <Link href="/license" target="_blank" />, button: t('send') }}
              />
            </div>
            <Button
              type="submit"
              className={styles.button}
              viewStyle="primary"
              size="medium"
              disabled={!isValid}
            >
              {t('send')}
            </Button>
          </div>
        </>
      )}
    </form>
  );
};

export default TeamRequestForm;
